import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const workplaceSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/workplace-injuries#webpage`,
      url: `${siteUrl}/workplace-injuries`,
      name: 'Indiana Workplace Injury Attorney | Workers Compensation Lawyer | Jun Law Firm',
      description:
        'Injured at work in Indiana? Your immigration status does not affect your right to workers compensation. Jun Law Firm protects immigrant workers rights. Free case review. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/workplace-injuries#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Workplace Injuries', item: `${siteUrl}/workplace-injuries` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/workplace-injuries#service`,
      name: 'Jun Law Firm - Workplace Injury Attorney Indiana',
      url: `${siteUrl}/workplace-injuries`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Indiana workplace injury and workers compensation attorney representing immigrant workers in construction, warehouse, restaurant, and manufacturing accident cases. Free case review.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Can undocumented immigrants file workers compensation claims in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. All injured workers in Indiana are entitled to workers compensation benefits regardless of immigration status. Your employer cannot deny your claim or threaten your status for filing.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to report a workplace injury in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Indiana law requires you to report workplace injuries to your employer within 30 days. Failure to report promptly may jeopardize your claim.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can my employer fire me for filing a workplace injury claim?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Employers in Indiana are prohibited from retaliating against workers who file workplace injury claims or report unsafe conditions.',
          },
        },
      ],
    },
  ],
};

export default function WorkplaceInjuries() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const injuryTypes = [
    {
      number: 'I',
      title: 'Construction Accidents',
      description: 'Falls from heights, scaffolding collapses, equipment failures, and other construction site injuries common among immigrant workers.'
    },
    {
      number: 'II',
      title: 'Warehouse Injuries',
      description: 'Forklift accidents, falling objects, repetitive strain injuries, and unsafe working conditions in distribution centers.'
    },
    {
      number: 'III',
      title: 'Restaurant & Kitchen Injuries',
      description: 'Burns, cuts, slip and falls, and other injuries in food service environments where many immigrants work.'
    },
    {
      number: 'IV',
      title: 'Manufacturing Accidents',
      description: 'Machine injuries, chemical exposure, repetitive motion injuries, and industrial accidents in factories.'
    },
    {
      number: 'V',
      title: 'Healthcare Worker Injuries',
      description: 'Back injuries from patient lifting, needlestick injuries, and workplace violence in medical facilities.'
    },
    {
      number: 'VI',
      title: 'Delivery Driver Accidents',
      description: 'Vehicle crashes, loading dock injuries, and other hazards faced by delivery and transportation workers.'
    }
  ];

  const yourRights = [
    {
      title: 'Right to Workers\' Compensation',
      description: 'All injured workers in Indiana are entitled to workers\' comp benefits regardless of immigration status.'
    },
    {
      title: 'Protection from Retaliation',
      description: 'Employers cannot fire or punish you for filing a workplace injury claim or reporting unsafe conditions.'
    },
    {
      title: 'Medical Treatment',
      description: 'You have the right to receive proper medical care for your work-related injuries at no cost to you.'
    },
    {
      title: 'Confidentiality',
      description: 'Your immigration status is protected and confidential in workers\' compensation proceedings.'
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Indiana Workplace Injury Attorney | Workers Compensation Lawyer | Jun Law Firm"
        description="Injured at work in Indiana? Your immigration status does not affect your right to workers compensation. Jun Law Firm protects immigrant workers rights. Free case review. (317) 701-2768."
        keywords="workplace injury attorney Indiana, workers compensation lawyer Fishers, construction accident attorney, immigrant workers rights Indiana, work injury lawyer"
        canonicalPath="/workplace-injuries"
        schema={workplaceSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Workers' Rights Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Workplace Injury <em className="font-light italic">Attorney</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Injured at work in Indiana? Your immigration status does not affect your right to workers' compensation benefits and medical treatment. Jun Law Firm protects immigrant workers' rights and fights for the benefits you deserve.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Free Case Review
              </button>
              <a 
                href="tel:+13177012768"
                className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Call: (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Injury Types */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Our Expertise
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Common <em className="italic">Workplace Injuries</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              We represent injured workers across all industries in Indiana, fighting for fair compensation and proper medical treatment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {injuryTypes.map((injury, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {injury.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {injury.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {injury.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-24 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Legal Protection
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your <em className="italic">Rights</em> as an Injured Worker
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Indiana law protects all injured workers, regardless of immigration status. Know your rights and don't let employers intimidate you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            {yourRights.map((right, index) => (
              <article key={index} className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {right.title}
                </h3>
                <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {right.description}
                </p>
              </article>
            ))}
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-900 p-12">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
              Important Notice
            </div>
            <h3 className="text-3xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Report Your Injury <em className="italic">Immediately</em>
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Indiana law requires you to report workplace injuries to your employer within 30 days. Failure to report promptly may jeopardize your claim. Even if your employer discourages reporting or threatens you, document your injury and contact Jun Law Firm immediately.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>01</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Report the injury in writing to your supervisor</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>02</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Seek medical attention and keep all records</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>03</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Take photos of the accident scene if possible</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>04</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Contact an attorney before signing any documents</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-gray-400 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
            Take Action Now
          </div>
          <h2 className="text-5xl md:text-6xl font-light mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Protect Your Rights After a <em className="italic">Workplace Injury</em>
          </h2>
          <div className="w-16 h-px bg-white mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
            Don't let your employer deny you the benefits you deserve. Contact Jun Law Firm today for a free consultation in your preferred language.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:+13177012768"
              className="bg-white text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Call: (317) 701-2768
            </a>
            <button 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }, 300);
              }}
              className="border-2 border-white text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Online Contact Form
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
